import { useMemo } from "preact/hooks";
import { BrandingProvider } from "@brickme/d2c-branding/src/browser/context.tsx";
import { getBrowserBrandingById } from "@brickme/d2c-branding/src/browser/get.ts";
import { I18nProvider } from "@brickme/frontend-common/src/i18n/translation.tsx";
import type { GtmService } from "~/frontend-common/gtm.ts";
import {
	AuthStateProvider,
	ShopifyGraphQlProvider,
	ApiGraphQlProvider,
	GtmServiceProvider,
} from "~/hooks/index.ts";
import { GorgiasProvider } from "~/hooks/gorgias.tsx";
import { FacebookProvider } from "~/contexts/facebook.tsx";
import { DomainProvider } from "~/contexts/domain.tsx";
import { ThemeProvider } from "~/contexts/theme.tsx";
import { LocationProvider } from "~/contexts/location.tsx";
import App from "./app.tsx";

type AppContainerProps = {
	readonly gtm: GtmService;
	readonly shopifyConfig: {
		readonly customDomain: string;
		readonly storefrontAccessToken: string;
	};
	readonly userPoolConfig: {
		readonly id: string;
		readonly appClientId: string;
	};
	readonly brandId: string;
	readonly apiUrl: string;
	readonly platformCookieDomain: string;
	readonly creatorAppUrl: string;
	readonly websiteUrl: string;
	readonly gorgiasId?: string;
	readonly domain: string;
	readonly originSite: string;
	// readonly countryRedirectRules: ReadonlyArray<CountryRedirectRule>;
	readonly ipInfoToken: string;
	// readonly defaultCountryCode: string;
	readonly facebookAppId?: string;
	readonly contactEmailAddress: string;
	// readonly theme: Theme;
};

function AppContainer({
	gtm,
	brandId,
	contactEmailAddress,
	shopifyConfig,
	userPoolConfig,
	apiUrl,
	originSite,
	ipInfoToken,
	platformCookieDomain,
	// defaultCountryCode,
	creatorAppUrl,
	websiteUrl,
	gorgiasId,
	domain,
	facebookAppId,
	// countryRedirectRules,
}: AppContainerProps) {
	const branding = useMemo(() => getBrowserBrandingById(brandId), [brandId]);
	return (
		<GtmServiceProvider gtm={gtm}>
			<I18nProvider
				translations={[
					{ code: "en", name: "English", translations: undefined },
				]}
			>
				<ThemeProvider
					creatorAppUrl={creatorAppUrl}
					websiteUrl={websiteUrl}
					contactEmailAddress={contactEmailAddress}
				>
					<AuthStateProvider
						cookiesSecure={window.location.protocol === "https"}
						cookieDomain={platformCookieDomain}
						userPoolId={userPoolConfig.id}
						appClientId={userPoolConfig.appClientId}
						originSite={originSite}
					>
						<ApiGraphQlProvider uri={apiUrl}>
							<ShopifyGraphQlProvider
								shopDomain={shopifyConfig.customDomain}
								storefrontAccessToken={shopifyConfig.storefrontAccessToken}
							>
								<LocationProvider ipInfoToken={ipInfoToken}>
									{/* <LocalisationProvider
								ipInfoToken={ipInfoToken}
								cookieDomain={platformCookieDomain}
								defaultCountryCode={defaultCountryCode}
								countryRedirectRules={countryRedirectRules}
							> */}
									<GorgiasProvider id={gorgiasId}>
										<BrandingProvider branding={branding}>
											<DomainProvider domain={domain}>
												<FacebookProvider appId={facebookAppId}>
													<App />
												</FacebookProvider>
											</DomainProvider>
										</BrandingProvider>
									</GorgiasProvider>
								</LocationProvider>
							</ShopifyGraphQlProvider>
						</ApiGraphQlProvider>
					</AuthStateProvider>
				</ThemeProvider>
			</I18nProvider>
		</GtmServiceProvider>
	);
}

export default AppContainer;
